import { AnyAction } from '@reduxjs/toolkit'
import { find, get, keyBy, uniqBy } from 'lodash'

import {
  CREATE_DRAFT_NOTIFICATION_SUCCESS,
  CREATE_NOTIFICATION_SUCCESS,
  UPDATE_DRAFT_NOTIFICATION_SUCCESS
} from '@/pages/SpacePublisherBuilderPage/actionTypes'
import { CHANGE_CURRENT_SPACE } from '@/store/actionTypes'

import {
  ADD_MEMBER_TO_DRAFT_NOTIFICATION_AUDIENCE,
  FETCH_TARGETING_AGGREGATE_SUGGESTIONS_SUCCESS,
  FETCH_TARGETING_RULE_SET_SUGGESTIONS_SUCCESS,
  NAVIGATE_STEP_DRAFT_NOTIFICATION,
  REMOVE_MEMBER_FROM_DRAFT_NOTIFICATION_AUDIENCE,
  RESET_BULK_TARGETING_STATUS,
  RESET_DRAFT_NOTIFICATION,
  RESOLVE_BULK_TARGETING_AGGREGATES_SUCCESS,
  RESOLVE_BULK_TARGETING_RULE_SETS_SUCCESS,
  SAVE_DRAFT_NOTIFICATION,
  START_DRAFT_NOTIFICATION
} from './actionTypes'

type Audience = { type: string; value: string; memberCount?: number }[]

const uniqueAudience = (audience: Audience) => uniqBy(audience, ({ type, value }) => `${type}|${value}`)

type PublisherState = {
  audience: Audience
  bulkTargeting: { audience: Audience; processed: number }
  draft?: any
  notification?: any
  suggestions: { aggregates: any; ruleSets: any }
  savedDraft?: any
}

const initialState: PublisherState = {
  audience: [],
  bulkTargeting: { audience: [], processed: 0 },
  draft: null,
  notification: null,
  suggestions: { aggregates: {}, ruleSets: {} }
}

export default (state = initialState, action: AnyAction): PublisherState => {
  switch (action.type) {
    case CHANGE_CURRENT_SPACE: {
      return initialState
    }
    case START_DRAFT_NOTIFICATION: {
      return {
        ...state,
        audience: action.audience || [],
        bulkTargeting: { ...initialState.bulkTargeting },
        draft: { activeStep: action.activeStep, notification: action.notification, settings: action.settings }
      }
    }

    case SAVE_DRAFT_NOTIFICATION: {
      return {
        ...state,
        draft: { activeStep: action.activeStep, notification: action.notification, settings: action.settings }
      }
    }

    case ADD_MEMBER_TO_DRAFT_NOTIFICATION_AUDIENCE: {
      let display = action.rule.value
      let memberCount = 1

      if (action.rule.type === 'filter') {
        const suggestion = get(state.suggestions.aggregates, [action.urn, action.rule.value], {})

        display = suggestion.term
        memberCount = suggestion.count
      } else if (action.rule.type === 'group') {
        const suggestion = find(get(state.suggestions.aggregates, action.urn, {}), { termId: action.rule.value })

        display = suggestion.term
        memberCount = suggestion.count
      }

      return { ...state, audience: [...state.audience, { ...action.rule, display, memberCount, urn: action.urn }] }
    }
    case REMOVE_MEMBER_FROM_DRAFT_NOTIFICATION_AUDIENCE: {
      return {
        ...state,
        audience: [...state.audience.slice(0, action.rowIndex), ...state.audience.slice(action.rowIndex + 1)]
      }
    }

    case NAVIGATE_STEP_DRAFT_NOTIFICATION: {
      return {
        ...state,
        bulkTargeting: { ...initialState.bulkTargeting },
        draft: { ...state.draft, activeStep: action.activeStep }
      }
    }

    case RESET_BULK_TARGETING_STATUS: {
      return { ...state, bulkTargeting: { ...initialState.bulkTargeting } }
    }

    case RESET_DRAFT_NOTIFICATION: {
      return { ...state, bulkTargeting: { ...initialState.bulkTargeting }, draft: null }
    }

    case CREATE_NOTIFICATION_SUCCESS: {
      return {
        ...state,
        audience: [],
        draft: null,
        suggestions: { ...initialState.suggestions }
      }
    }

    case CREATE_DRAFT_NOTIFICATION_SUCCESS:
    case UPDATE_DRAFT_NOTIFICATION_SUCCESS: {
      return {
        ...state,
        savedDraft: action.response
      }
    }

    case FETCH_TARGETING_AGGREGATE_SUGGESTIONS_SUCCESS: {
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          aggregates: { ...state.suggestions.aggregates, [action.urn]: keyBy(action.response.resources, 'term') }
        }
      }
    }

    case FETCH_TARGETING_RULE_SET_SUGGESTIONS_SUCCESS: {
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          ruleSets: {
            ...state.suggestions.ruleSets,
            [action.ruleSet]: action.response.resources.map(({ id }: { id: string }) => id)
          }
        }
      }
    }

    case RESOLVE_BULK_TARGETING_AGGREGATES_SUCCESS: {
      return {
        ...state,
        audience: action.addToAudience
          ? uniqueAudience([
              ...state.audience,
              ...action.response.resources.map(({ count: memberCount, resourceType, term, termId }: any) => ({
                display: term,
                memberCount,
                type: resourceType === 'Group' ? 'group' : 'filter',
                urn: action.urn,
                value: resourceType === 'Group' ? termId : term
              }))
            ])
          : state.audience,
        bulkTargeting: {
          ...state.bulkTargeting,
          audience: [
            ...state.bulkTargeting.audience,
            ...action.response.resources.map(({ count: memberCount, resourceType, term, termId }: any) => ({
              display: term,
              memberCount,
              type: resourceType === 'Group' ? 'group' : 'filter',
              urn: action.urn,
              value: resourceType === 'Group' ? termId : `${action.urn} eq ${JSON.stringify(term)}`
            }))
          ],
          processed: state.bulkTargeting.processed + action.processed
        }
      }
    }

    case RESOLVE_BULK_TARGETING_RULE_SETS_SUCCESS: {
      const mapRuleSetResult = ({ id }: { id: string }) => ({
        display: id,
        memberCount: 1,
        type: 'guid',
        urn: action.urn,
        value: id
      })

      return {
        ...state,
        audience: action.addToAudience
          ? uniqueAudience([...state.audience, ...action.response.resources.map(mapRuleSetResult)])
          : state.audience,
        bulkTargeting: {
          ...state.bulkTargeting,
          audience: [...state.bulkTargeting.audience, ...action.response.resources.map(mapRuleSetResult)],
          processed: state.bulkTargeting.processed + action.processed
        }
      }
    }

    default: {
      return state
    }
  }
}
